import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import MainLayout from '../components/MainLayout';
import { motion, AnimatePresence } from 'framer-motion';

export default function StockManagementPage() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [restaurantId, setRestaurantId] = useState(null);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [loading, setLoading] = useState(true);

    const [ingredients, setIngredients] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [stockHistory, setStockHistory] = useState([]);

    const [showIngModal, setShowIngModal] = useState(false);
    const [showAdjustModal, setShowAdjustModal] = useState(false);

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data: rest } = await supabase.from('restaurants').select('id, plan').eq('owner_id', user.id).single();
            if (rest) {
                setRestaurantId(rest.id);
                setCurrentPlan(rest.plan || null);
                // Only fetch stock data if on pro/enterprise
                if (rest.plan === 'pro' || rest.plan === 'enterprise') {
                    await fetchData(rest.id);
                } else {
                    setLoading(false);
                }
            } else {
                setLoading(false);
            }
        };
        init();
    }, []);

    const fetchData = async (rId) => {
        setLoading(true);
        try {
            const [ingRes, recRes, menuRes, histRes] = await Promise.all([
                supabase.from('ingredients').select('*').eq('restaurant_id', rId).order('name'),
                supabase.from('recipe_items').select('*, ingredients!inner(*)').eq('ingredients.restaurant_id', rId),
                supabase.from('menu_items').select('id, name, is_available').eq('restaurant_id', rId).order('name'),
                supabase.from('stock_adjustments').select('*, ingredients!inner(*)').eq('ingredients.restaurant_id', rId).order('created_at', { ascending: false }).limit(200),
            ]);

            setIngredients(ingRes.data || []);
            setRecipes(recRes.data || []);
            setMenuItems(menuRes.data || []);
            setStockHistory(histRes.data || []);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const TABS = [
        { id: 'dashboard', label: 'Overview', icon: 'dashboard' },
        { id: 'ingredients', label: 'Ingredients', icon: 'kitchen' },
        { id: 'recipes', label: 'Recipes', icon: 'receipt_long' },
        { id: 'history', label: 'History', icon: 'history' },
    ];

    // ── Plan gate: show upgrade prompt for non-pro users ──
    if (!loading && currentPlan !== 'pro' && currentPlan !== 'enterprise') {
        return (
            <MainLayout activeLink="Stock & Inventory" title="Stock Management">
                <main className="flex-1 overflow-y-auto flex items-center justify-center p-8">
                    <div className="text-center max-w-md">
                        <div className="w-20 h-20 bg-charcoal dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                            <span className="material-symbols-outlined text-primary text-4xl">inventory_2</span>
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white mb-3">Stock Management</h1>
                        <p className="text-slate-500 dark:text-slate-400 font-medium mb-2">This feature is available on the <strong className="text-slate-800 dark:text-slate-200">Pro</strong> and <strong className="text-slate-800 dark:text-slate-200">Enterprise</strong> plans.</p>
                        <p className="text-slate-400 dark:text-slate-500 text-sm mb-8">Upgrade to automatically track ingredients, map recipes, and get low-stock alerts.</p>
                        <div className="flex gap-3 justify-center">
                            <button
                                onClick={() => navigate('/subscription')}
                                className="px-8 py-3.5 bg-primary text-charcoal font-black rounded-2xl hover:bg-primary/90 active:scale-95 transition-colors shadow-lg shadow-primary/20"
                            >
                                Upgrade Plan →
                            </button>
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="px-6 py-3.5 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-bold rounded-2xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                            >
                                Go Back
                            </button>
                        </div>
                    </div>
                </main>
            </MainLayout>
        );
    }

    return (
        <>
            <MainLayout activeLink="Stock & Inventory" title="Stock Management">
                <main className="flex-1 overflow-y-auto p-8">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Stock Management</h1>
                            <p className="text-slate-500 dark:text-slate-400 font-medium">Manage ingredients, set recipes, and automate stock deduction.</p>
                        </div>
                        <div className="flex gap-3">
                            <button onClick={() => setShowIngModal(true)} className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-bold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm">add</span> New Ingredient
                            </button>
                            <button onClick={() => setShowAdjustModal(true)} className="px-4 py-2 bg-primary text-charcoal font-bold rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm">inventory</span> Adjust Stock
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 mb-6 border-b border-slate-200 dark:border-slate-800 pb-px overflow-x-auto no-scrollbar">
                        {TABS.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-2 py-3 font-bold text-sm flex items-center gap-2 transition-colors border-b-2 whitespace-nowrap
                                    ${activeTab === tab.id 
                                        ? 'text-primary border-primary' 
                                        : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-200'}`}
                            >
                                <span className="material-symbols-outlined text-lg">{tab.icon}</span>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="flex justify-center p-20"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
                    ) : (
                        <div className="pb-20">
                            {activeTab === 'dashboard' && <StockDashboard ingredients={ingredients} history={stockHistory} />}
                            {activeTab === 'ingredients' && <IngredientsTab ingredients={ingredients} onReload={() => fetchData(restaurantId)} />}
                            {activeTab === 'recipes' && <RecipesTab recipes={recipes} menuItems={menuItems} ingredients={ingredients} onReload={() => fetchData(restaurantId)} />}
                            {activeTab === 'history' && <HistoryTab history={stockHistory} />}
                        </div>
                    )}
                </main>
            </MainLayout>

            {/* Modals */}
            <AnimatePresence>
                {showIngModal && <IngredientModal rId={restaurantId} onClose={() => setShowIngModal(false)} onSaved={() => fetchData(restaurantId)} />}
                {showAdjustModal && <AdjustStockModal rId={restaurantId} ingredients={ingredients} onClose={() => setShowAdjustModal(false)} onSaved={() => fetchData(restaurantId)} />}
            </AnimatePresence>
        </>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// TABS
// ─────────────────────────────────────────────────────────────────────────────

function StockDashboard({ ingredients, history }) {
    const { lowStock, totalValue, topWasteByCost, deadStock } = useMemo(() => {
        // 1. Low Stock (Actionable)
        const low = ingredients.filter(i => i.current_stock <= i.min_threshold).map(i => ({
            ...i,
            suggestedOrder: Math.max(0, (i.min_threshold * 2) - i.current_stock) // simple par level heuristic
        }));

        // 2. Total Value
        const val = ingredients.reduce((sum, i) => sum + ((i.current_stock || 0) * (i.cost_per_unit || 0)), 0);
        
        // 3. Financial Waste (Cost * Qty)
        const wasteEvents = history.filter(h => h.type === 'waste');
        const wasteCosts = {};
        wasteEvents.forEach(h => {
            if (h.ingredients) {
                const cost = h.ingredients.cost_per_unit || 0;
                wasteCosts[h.ingredients.name] = {
                    qty: (wasteCosts[h.ingredients.name]?.qty || 0) + h.quantity,
                    loss: (wasteCosts[h.ingredients.name]?.loss || 0) + (h.quantity * cost),
                    unit: h.ingredients.unit
                };
            }
        });
        const wastedArr = Object.entries(wasteCosts).sort((a,b) => b[1].loss - a[1].loss);
        const topWaste = wastedArr.length > 0 ? { name: wastedArr[0][0], ...wastedArr[0][1] } : null;

        // 4. Dead Stock (Items with high stock, > 2x min threshold, but taking up capital)
        // Since we don't have usage velocity without complex queries, we'll proxy it by high capital tied up
        const dead = [...ingredients]
            .filter(i => i.current_stock > i.min_threshold * 2 && i.cost_per_unit > 0)
            .map(i => ({
                ...i,
                capitalTied: i.current_stock * i.cost_per_unit
            }))
            .sort((a,b) => b.capitalTied - a.capitalTied)
            .slice(0, 3);

        return { lowStock: low, totalValue: val, topWasteByCost: topWaste, deadStock: dead };
    }, [ingredients, history]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
            
            {/* LEFT COLUMN: OPERATIONAL ACTION (8 cols) */}
            <div className="xl:col-span-8 space-y-6">
                
                {/* Low Stock Table */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm flex flex-col">
                    <div className="p-5 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50">
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-red-500">error</span>
                            <h3 className="font-bold text-slate-900 dark:text-white">Action Required: Low Stock</h3>
                        </div>
                        <span className="px-2.5 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-bold rounded-lg">{lowStock.length} items</span>
                    </div>
                    
                    {lowStock.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left whitespace-nowrap">
                                <thead className="bg-slate-50 dark:bg-slate-950/50 border-b border-slate-100 dark:border-slate-800">
                                    <tr>
                                        <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ingredient</th>
                                        <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-right">Current Stock</th>
                                        <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-right">Min Alert</th>
                                        <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide text-right">Suggested Order</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                                    {lowStock.map(i => (
                                        <tr key={i.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors">
                                            <td className="px-5 py-3 font-semibold text-slate-900 dark:text-slate-200">{i.name}</td>
                                            <td className="px-5 py-3 text-right">
                                                <span className="text-red-600 dark:text-red-400 font-bold tabular-nums">{i.current_stock}</span>
                                                <span className="text-xs text-slate-400 ml-1">{i.unit}</span>
                                            </td>
                                            <td className="px-5 py-3 text-right text-sm text-slate-500 dark:text-slate-400 tabular-nums">{i.min_threshold} {i.unit}</td>
                                            <td className="px-5 py-3 text-right">
                                                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold tabular-nums bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-md">
                                                    +{i.suggestedOrder} <span className="text-[10px]">{i.unit}</span>
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-8 text-center text-slate-500 dark:text-slate-400 flex flex-col items-center">
                            <span className="material-symbols-outlined text-4xl mb-2 opacity-50">check_circle</span>
                            <p className="font-semibold text-sm">All stock levels are healthy.</p>
                        </div>
                    )}
                </div>

            </div>

            {/* RIGHT COLUMN: FINANCIAL HEALTH (4 cols) */}
            <div className="xl:col-span-4 space-y-6">
                
                {/* Total Value Card */}
                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">account_balance</span> Total Inventory Value
                        </div>
                        <div className="text-4xl font-bold text-slate-900 dark:text-white tabular-nums tracking-tight">₹{totalValue.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                        <div className="mt-3 text-xs font-semibold text-slate-400 dark:text-slate-500">Across {ingredients.length} active ingredients</div>
                    </div>
                    <div className="absolute -right-6 -bottom-6 text-slate-50 dark:text-slate-800/50">
                        <span className="material-symbols-outlined text-9xl">inventory_2</span>
                    </div>
                </div>

                {/* Financial Waste Card */}
                {topWasteByCost && (
                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 p-6 rounded-2xl shadow-sm">
                        <div className="text-xs font-bold text-amber-700 dark:text-amber-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">trending_down</span> Top Financial Waste
                        </div>
                        <div className="flex justify-between items-end mb-2">
                            <div className="font-bold text-slate-900 dark:text-slate-200">{topWasteByCost.name}</div>
                            <div className="text-xl font-bold text-red-600 dark:text-red-400 tabular-nums">₹{topWasteByCost.loss.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                        </div>
                        <div className="w-full bg-amber-200/50 dark:bg-amber-900/30 rounded-full h-1.5 mb-2">
                            <div className="bg-red-500 h-1.5 rounded-full w-full"></div>
                        </div>
                        <div className="text-xs font-semibold text-amber-700/80 dark:text-amber-500/80 text-right">
                            {topWasteByCost.qty} {topWasteByCost.unit} wasted
                        </div>
                    </div>
                )}

                {/* Dead Stock / Capital Tied Up */}
                {deadStock.length > 0 && (
                    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-sm">
                        <div className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">lock</span> High Capital Tied Up
                        </div>
                        <div className="space-y-4">
                            {deadStock.map(i => (
                                <div key={i.id} className="flex justify-between items-center">
                                    <div>
                                        <div className="font-semibold text-sm text-slate-800 dark:text-slate-200">{i.name}</div>
                                        <div className="text-xs text-slate-500 mt-0.5">{i.current_stock} {i.unit} in stock</div>
                                    </div>
                                    <div className="font-bold text-slate-700 dark:text-slate-300 tabular-nums">
                                        ₹{i.capitalTied.toLocaleString(undefined, {maximumFractionDigits: 0})}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
}

function IngredientsTab({ ingredients, onReload }) {
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name'); // name, stock, cost
    const [sortAsc, setSortAsc] = useState(true);

    const filtered = useMemo(() => {
        let items = [...ingredients];
        if (search.trim()) {
            items = items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()));
        }
        items.sort((a, b) => {
            let valA, valB;
            if (sortField === 'name') { valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); }
            else if (sortField === 'stock') { valA = a.current_stock; valB = b.current_stock; }
            else if (sortField === 'cost') { valA = a.cost_per_unit || 0; valB = b.cost_per_unit || 0; }
            
            if (valA < valB) return sortAsc ? -1 : 1;
            if (valA > valB) return sortAsc ? 1 : -1;
            return 0;
        });
        return items;
    }, [ingredients, search, sortField, sortAsc]);

    const handleSort = (field) => {
        if (sortField === field) setSortAsc(!sortAsc);
        else { setSortField(field); setSortAsc(true); }
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return <span className="material-symbols-outlined text-[14px] opacity-30">unfold_more</span>;
        return <span className="material-symbols-outlined text-[14px]">{sortAsc ? 'expand_less' : 'expand_more'}</span>;
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
            {/* Toolbar */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/50 rounded-t-2xl">
                <div className="relative w-full max-w-xs">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
                    <input 
                        type="text" 
                        placeholder="Search ingredients..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-medium outline-none focus:border-primary dark:text-white transition-colors"
                    />
                </div>
                <div className="text-xs font-semibold text-slate-500 dark:text-slate-400">
                    Showing {filtered.length} items
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left whitespace-nowrap min-w-[600px]">
                    <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                        <tr>
                            <th onClick={() => handleSort('name')} className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                                <div className="flex items-center gap-1">Ingredient <SortIcon field="name" /></div>
                            </th>
                            <th onClick={() => handleSort('stock')} className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors text-right">
                                <div className="flex items-center justify-end gap-1">Current Stock <SortIcon field="stock" /></div>
                            </th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-right">Min Alert</th>
                            <th onClick={() => handleSort('cost')} className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase cursor-pointer hover:text-slate-700 dark:hover:text-slate-200 transition-colors text-right">
                                <div className="flex items-center justify-end gap-1">Cost/Unit <SortIcon field="cost" /></div>
                            </th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filtered.length > 0 ? (
                            filtered.map(ing => {
                                const isLow = ing.current_stock <= ing.min_threshold;
                                return (
                                    <tr key={ing.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="px-5 py-3">
                                            <div className="font-bold text-slate-900 dark:text-white">{ing.name}</div>
                                            {ing.category && <div className="text-xs text-slate-500 dark:text-slate-400">{ing.category}</div>}
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <span className="text-base font-bold text-slate-900 dark:text-white tabular-nums">{ing.current_stock}</span> 
                                            <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">{ing.unit}</span>
                                        </td>
                                        <td className="px-5 py-3 text-sm font-semibold text-slate-500 dark:text-slate-400 text-right tabular-nums">
                                            {ing.min_threshold} <span className="text-xs">{ing.unit}</span>
                                        </td>
                                        <td className="px-5 py-3 text-sm font-semibold text-slate-500 dark:text-slate-400 text-right tabular-nums">
                                            ₹{ing.cost_per_unit || 0}
                                        </td>
                                        <td className="px-5 py-3 text-center">
                                            {isLow ? (
                                                <span className="inline-flex items-center px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-[10px] font-bold rounded uppercase tracking-wide">Low Stock</span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-[10px] font-bold rounded uppercase tracking-wide">Healthy</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="5" className="p-8 text-center">
                                    <div className="flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                                        <span className="material-symbols-outlined text-4xl mb-3 opacity-50">kitchen</span>
                                        <p className="font-semibold text-sm">{ingredients.length === 0 ? "No ingredients added yet." : "No ingredients match your search."}</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RecipesTab({ recipes, menuItems, ingredients, onReload }) {
    const [selectedDish, setSelectedDish] = useState('');
    const [selectedIng, setSelectedIng] = useState('');
    const [qty, setQty] = useState('');

    const addRecipeItem = async () => {
        if (!selectedDish || !selectedIng || !qty) return;
        try {
            await supabase.from('recipe_items').insert({
                menu_item_id: selectedDish,
                ingredient_id: selectedIng,
                quantity_used: parseFloat(qty)
            });
            setSelectedIng(''); setQty('');
            onReload();
        } catch (e) { alert(e.message); }
    };

    const deleteItem = async (id) => {
        await supabase.from('recipe_items').delete().eq('id', id);
        onReload();
    };

    const grouped = menuItems.map(m => ({
        ...m,
        items: recipes.filter(r => r.menu_item_id === m.id)
    })).filter(m => m.items.length > 0 || selectedDish === m.id);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-sm h-fit sticky top-8">
                <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2">Map Ingredients</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">Select a menu item and link the ingredients that should be auto-deducted per order.</p>
                
                <div className="space-y-4">
                    <div>
                        <label htmlFor="dish-select" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Select Dish</label>
                        <select id="dish-select" className="w-full mt-1.5 p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium outline-none focus:border-primary dark:text-white" value={selectedDish} onChange={e => setSelectedDish(e.target.value)}>
                            <option value="">-- Choose a dish --</option>
                            {menuItems.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </div>

                    {selectedDish && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-4">
                            <div>
                                <label htmlFor="ing-select" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ingredient to deduct</label>
                                <select id="ing-select" className="w-full mt-1.5 p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium outline-none focus:border-primary dark:text-white" value={selectedIng} onChange={e => setSelectedIng(e.target.value)}>
                                    <option value="">-- Choose ingredient --</option>
                                    {ingredients.map(i => <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="qty-input" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Quantity Required</label>
                                <div className="flex gap-2 items-center mt-1.5">
                                    <input id="qty-input" type="number" step="0.01" className="w-full p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg text-sm font-medium outline-none focus:border-primary dark:text-white tabular-nums" placeholder="e.g. 0.5" value={qty} onChange={e => setQty(e.target.value)} />
                                    <span className="text-xs font-semibold text-slate-400 w-12">{ingredients.find(i => i.id === selectedIng)?.unit || 'units'}</span>
                                </div>
                            </div>
                            <button onClick={addRecipeItem} className="w-full py-2.5 bg-slate-900 dark:bg-primary text-white dark:text-charcoal text-sm font-bold rounded-lg mt-2 hover:bg-slate-800 dark:hover:bg-primary/90 transition-colors">Add to Recipe</button>
                        </motion.div>
                    )}
                </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
                {grouped.length === 0 ? (
                    <div className="text-center p-12 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 flex flex-col items-center">
                        <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                            <span className="material-symbols-outlined text-3xl text-slate-400">link</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white">No Recipes Mapped</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Select a dish on the left to start mapping ingredients for automatic deduction.</p>
                    </div>
                ) : (
                    grouped.map(dish => (
                        <div key={dish.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                            <div className="bg-slate-50 dark:bg-slate-900/50 px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                                <h4 className="font-bold text-slate-900 dark:text-white text-sm">{dish.name}</h4>
                                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700">{dish.items.length} items</span>
                            </div>
                            <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                {dish.items.map(item => (
                                    <div key={item.id} className="p-3.5 flex justify-between items-center hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 font-bold text-xs uppercase">
                                                {item.ingredients?.name?.charAt(0)}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm text-slate-900 dark:text-slate-200">{item.ingredients?.name}</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Deducts <strong className="text-slate-700 dark:text-slate-300 tabular-nums">{item.quantity_used} {item.ingredients?.unit}</strong> per order</div>
                                            </div>
                                        </div>
                                        <button onClick={() => deleteItem(item.id)} className="w-8 h-8 flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" aria-label="Remove ingredient">
                                            <span className="material-symbols-outlined text-[18px]">close</span>
                                        </button>
                                    </div>
                                ))}
                                {dish.items.length === 0 && <div className="p-5 text-sm text-slate-400 italic text-center">No ingredients mapped to this dish yet.</div>}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function HistoryTab({ history }) {
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('all');

    const filtered = useMemo(() => {
        return history.filter(h => {
            const matchesSearch = h.ingredients?.name?.toLowerCase().includes(search.toLowerCase());
            const matchesType = filterType === 'all' || h.type === filterType;
            return matchesSearch && matchesType;
        });
    }, [history, search, filterType]);

    const typeColors = {
        'add': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
        'deduct': 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300',
        'waste': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
        'damage': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col">
            {/* Toolbar */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-4 justify-between items-center bg-slate-50/50 dark:bg-slate-900/50 rounded-t-2xl">
                <div className="relative w-full sm:w-64">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</span>
                    <input 
                        type="text" 
                        placeholder="Search history..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm font-medium outline-none focus:border-primary dark:text-white transition-colors"
                    />
                </div>
                <div className="flex gap-2">
                    {['all', 'add', 'deduct', 'waste'].map(t => (
                        <button 
                            key={t}
                            onClick={() => setFilterType(t)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors
                                ${filterType === t 
                                    ? 'bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900' 
                                    : 'bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left whitespace-nowrap min-w-[600px]">
                    <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                        <tr>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Date & Time</th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Ingredient</th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-right">Change</th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase text-center">Type</th>
                            <th className="px-5 py-3 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">Reference</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {filtered.length > 0 ? (
                            filtered.map(h => (
                                <tr key={h.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="px-5 py-3 text-sm text-slate-500 dark:text-slate-400 font-medium tabular-nums">{new Date(h.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</td>
                                    <td className="px-5 py-3 font-semibold text-slate-900 dark:text-slate-200 text-sm">{h.ingredients?.name}</td>
                                    <td className="px-5 py-3 text-right">
                                        <span className={`text-base font-bold tabular-nums ${h.type === 'add' ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                                            {h.type === 'add' ? '+' : '-'}{h.quantity}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-center">
                                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold rounded uppercase tracking-wide ${typeColors[h.type] || 'bg-slate-100 text-slate-700'}`}>
                                            {h.type}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400 max-w-[200px] truncate" title={h.reason}>{h.reason || '-'}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="5" className="p-8 text-center">
                                    <div className="flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
                                        <span className="material-symbols-outlined text-4xl mb-3 opacity-50">history</span>
                                        <p className="font-semibold text-sm">{history.length === 0 ? "No stock history recorded yet." : "No records match your filters."}</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODALS
// ─────────────────────────────────────────────────────────────────────────────

function IngredientModal({ rId, onClose, onSaved }) {
    const [name, setName] = useState('');
    const [unit, setUnit] = useState('kg');
    const [minStock, setMinStock] = useState('');
    const [cost, setCost] = useState('');

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const save = async () => {
        if (!name || !minStock) return;
        try {
            await supabase.from('ingredients').insert({
                restaurant_id: rId,
                name,
                unit: unit,
                min_threshold: parseFloat(minStock),
                cost_per_unit: parseFloat(cost) || 0
            });
            onSaved();
            onClose();
        } catch (e) { alert(e.message); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button type="button" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close modal" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 w-full max-w-md relative z-10 shadow-2xl">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">New Ingredient</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="ing-name" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Ingredient Name</label>
                        <input id="ing-name" type="text" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white transition-colors" placeholder="e.g. Paneer" value={name} onChange={e => setName(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="ing-unit" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Unit Type</label>
                            <select id="ing-unit" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white transition-colors" value={unit} onChange={e => setUnit(e.target.value)}>
                                <option value="kg">kg</option>
                                <option value="g">grams (g)</option>
                                <option value="L">Liters (L)</option>
                                <option value="ml">ml</option>
                                <option value="pcs">Pieces (pcs)</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="ing-min" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Min Stock Alert</label>
                            <input id="ing-min" type="number" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white tabular-nums transition-colors" placeholder="e.g. 5" value={minStock} onChange={e => setMinStock(e.target.value)} />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="ing-cost" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Cost Per Unit (Optional)</label>
                        <input id="ing-cost" type="number" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white tabular-nums transition-colors" placeholder="₹" value={cost} onChange={e => setCost(e.target.value)} />
                    </div>
                    <button onClick={save} className="w-full py-3.5 mt-2 bg-slate-900 dark:bg-primary text-white dark:text-charcoal text-sm font-bold rounded-xl hover:bg-slate-800 dark:hover:bg-primary/90 transition-colors">Save Ingredient</button>
                </div>
            </motion.div>
        </div>
    );
}

function AdjustStockModal({ rId, ingredients, onClose, onSaved }) {
    const [ingId, setIngId] = useState('');
    const [type, setType] = useState('purchase');
    const [qty, setQty] = useState('');

    useEffect(() => {
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    const save = async () => {
        if (!ingId || !qty) return;
        let dbType = 'add';
        if (type === 'purchase') dbType = 'add';
        if (type === 'waste') dbType = 'waste';
        if (type === 'adjustment') dbType = 'deduct';

        try {
            await supabase.from('stock_adjustments').insert({
                ingredient_id: ingId,
                type: dbType,
                quantity: parseFloat(qty),
                reason: 'Manual Adjustment'
            });
            onSaved();
            onClose();
        } catch (e) { alert(e.message); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button type="button" className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close modal" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-8 w-full max-w-md relative z-10 shadow-2xl">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Adjust Stock</h2>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="adj-ing" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Select Ingredient</label>
                        <select id="adj-ing" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white transition-colors" value={ingId} onChange={e => setIngId(e.target.value)}>
                            <option value="">-- Choose --</option>
                            {ingredients.map(i => <option key={i.id} value={i.id}>{i.name} (Current: {i.current_stock} {i.unit})</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="adj-type" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Adjustment Type</label>
                            <select id="adj-type" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white transition-colors" value={type} onChange={e => setType(e.target.value)}>
                                <option value="purchase">Add (Purchase)</option>
                                <option value="waste">Reduce (Waste)</option>
                                <option value="adjustment">Manual Correction</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="adj-qty" className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Quantity {type === 'purchase' ? 'to Add' : 'to Deduct'}</label>
                            <input id="adj-qty" type="number" step="0.01" className="w-full mt-1.5 p-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:border-primary dark:text-white tabular-nums transition-colors" placeholder="e.g. 10" value={qty} onChange={e => setQty(e.target.value)} />
                        </div>
                    </div>
                    <button onClick={save} className="w-full py-3.5 mt-2 bg-slate-900 dark:bg-primary text-white dark:text-charcoal text-sm font-bold rounded-xl hover:bg-slate-800 dark:hover:bg-primary/90 transition-colors">Confirm Adjustment</button>
                </div>
            </motion.div>
        </div>
    );
}
